import {
  ASSET_TYPES,
  BOOLEAN_FIELDS,
  CONDITIONS,
  NUMERIC_FIELDS,
  STATUSES,
  getRequiredFields,
  normalizeCondition,
  type ImportMode,
} from "./fields";

export interface ImportError {
  row: number;
  field: string;
  message: string;
}

export interface ParsedAsset {
  [key: string]: string;
}

export function validateAssetRows({
  parsedData,
  columnMapping,
  importMode,
  accessiblePropertyIds,
}: {
  parsedData: ParsedAsset[];
  columnMapping: Record<string, string>;
  importMode: ImportMode;
  accessiblePropertyIds: Set<string>;
}): ImportError[] {
  const errors: ImportError[] = [];
  const requiredFields = getRequiredFields(importMode);

  parsedData.forEach((row, index) => {
    // Check required fields
    requiredFields.forEach((field) => {
      const columnName = columnMapping[field.key];
      const value = columnName ? row[columnName]?.trim() : "";

      if (!value) {
        errors.push({
          row: index + 1,
          field: field.label,
          message: `${field.label} is required`,
        });
      }
    });

    // Validate property_id for multi-property mode
    if (importMode === "multi") {
      const propertyIdColumn = columnMapping["property_id"];
      const propertyId = propertyIdColumn ? row[propertyIdColumn]?.trim() : "";

      if (!propertyId) {
        errors.push({
          row: index + 1,
          field: "Property ID",
          message: "Property ID is required for multi-property import",
        });
      } else if (!accessiblePropertyIds.has(propertyId)) {
        const availableProps = Array.from(accessiblePropertyIds).slice(0, 3).join(", ");
        errors.push({
          row: index + 1,
          field: "Property ID",
          message: `Invalid property ID "${propertyId}". Must be one of your accessible properties (e.g., ${availableProps}...)`,
        });
      }
    }

    // Validate asset type
    const assetTypeColumn = columnMapping["asset_type"];
    const assetType = assetTypeColumn ? row[assetTypeColumn]?.trim() : "";
    if (assetType && !ASSET_TYPES.includes(assetType)) {
      errors.push({
        row: index + 1,
        field: "Asset Type",
        message: `Invalid asset type "${assetType}". Must be one of: ${ASSET_TYPES.join(", ")}`,
      });
    }

    // Validate status
    const statusColumn = columnMapping["status"];
    const status = statusColumn ? row[statusColumn]?.trim() : "";
    if (status && !STATUSES.includes(status)) {
      errors.push({
        row: index + 1,
        field: "Status",
        message: `Invalid status "${status}". Must be one of: ${STATUSES.join(", ")}`,
      });
    }

    // Validate condition (with normalization)
    const conditionColumn = columnMapping["condition"];
    const condition = conditionColumn ? row[conditionColumn]?.trim() : "";
    if (condition) {
      const normalizedCondition = normalizeCondition(condition);
      if (!CONDITIONS.includes(normalizedCondition)) {
        errors.push({
          row: index + 1,
          field: "Condition",
          message: `Invalid condition "${condition}". Must be one of: ${CONDITIONS.join(", ")} (will auto-convert common values)`,
        });
      }
    }

    // Validate numeric fields
    NUMERIC_FIELDS.forEach((field) => {
      const columnName = columnMapping[field];
      const value = columnName ? row[columnName]?.trim() : "";
      if (value && isNaN(Number(value))) {
        errors.push({
          row: index + 1,
          field,
          message: `${field} must be a valid number (got "${value}")`,
        });
      }
    });

    // Validate boolean fields
    BOOLEAN_FIELDS.forEach((field) => {
      const columnName = columnMapping[field];
      const value = columnName ? row[columnName]?.trim().toUpperCase() : "";
      if (value && !["TRUE", "FALSE", ""].includes(value)) {
        errors.push({
          row: index + 1,
          field,
          message: `${field} must be TRUE or FALSE (got "${value}")`,
        });
      }
    });
  });

  return errors;
}
